import { z } from 'zod';

export const MorningPhraseKind = z.enum(['GREETING', 'CTA']);
export type MorningPhraseKind = z.infer<typeof MorningPhraseKind>;

// --- settings ---
export const MorningSettingsDto = z.object({
  weatherEnabled: z.boolean(),
  eventsEnabled: z.boolean(),
  eventsLimit: z.number().int().positive().max(10),
});
export type MorningSettingsDto = z.infer<typeof MorningSettingsDto>;

export const MorningSettingsUpdateInput = z
  .object({
    weatherEnabled: z.boolean(),
    eventsEnabled: z.boolean(),
    eventsLimit: z.number().int().positive().max(10),
  })
  .partial();
export type MorningSettingsUpdateInput = z.infer<typeof MorningSettingsUpdateInput>;

// --- phrases ---
export const MorningPhraseDto = z.object({
  id: z.uuid(),
  cityId: z.uuid().nullable(),
  kind: MorningPhraseKind,
  text: z.string(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
});
export type MorningPhraseDto = z.infer<typeof MorningPhraseDto>;

export const MorningPhraseCreateInput = z.object({
  cityId: z.uuid().nullable().optional(),
  kind: MorningPhraseKind,
  text: z.string().min(1).max(200),
  sortOrder: z.number().int().optional(),
});
export type MorningPhraseCreateInput = z.infer<typeof MorningPhraseCreateInput>;

export const MorningPhraseUpdateInput = z
  .object({
    text: z.string().min(1).max(200),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
  })
  .partial();
export type MorningPhraseUpdateInput = z.infer<typeof MorningPhraseUpdateInput>;

// --- images ---
export const MorningImageDto = z.object({
  id: z.uuid(),
  cityId: z.uuid().nullable(),
  url: z.string(),
  label: z.string().nullable(),
  isActive: z.boolean(),
  sortOrder: z.number().int(),
});
export type MorningImageDto = z.infer<typeof MorningImageDto>;

export const MorningImageUpdateInput = z
  .object({
    label: z.string().max(120).nullable(),
    isActive: z.boolean(),
    sortOrder: z.number().int(),
  })
  .partial();
export type MorningImageUpdateInput = z.infer<typeof MorningImageUpdateInput>;

// --- city config ---
export const MorningCityConfigDto = z.object({
  cityId: z.uuid(),
  useGlobalImages: z.boolean(),
  useGlobalGreetings: z.boolean(),
  useGlobalCtas: z.boolean(),
});
export type MorningCityConfigDto = z.infer<typeof MorningCityConfigDto>;

export const MorningCityConfigUpdateInput = z
  .object({
    useGlobalImages: z.boolean(),
    useGlobalGreetings: z.boolean(),
    useGlobalCtas: z.boolean(),
  })
  .partial();
export type MorningCityConfigUpdateInput = z.infer<typeof MorningCityConfigUpdateInput>;

// --- holidays ---
const holidayBase = {
  cityId: z.uuid().nullable().optional(),
  countryCode: z.string().length(2).nullable().optional(),
  date: z.iso.date(),
  greeting: z.string().max(200).nullable().optional(),
  cta: z.string().max(200).nullable().optional(),
  label: z.string().max(120).nullable().optional(),
};

function exactlyOneScope(v: {
  cityId?: string | null | undefined;
  countryCode?: string | null | undefined;
}): boolean {
  const hasCity = v.cityId != null;
  const hasCountry = v.countryCode != null;
  return hasCity !== hasCountry; // XOR
}

export const MorningHolidayCreateInput = z
  .object(holidayBase)
  .refine(exactlyOneScope, { message: 'ровно один из cityId/countryCode обязателен' });
export type MorningHolidayCreateInput = z.infer<typeof MorningHolidayCreateInput>;

export const MorningHolidayUpdateInput = z
  .object({
    date: z.iso.date(),
    greeting: z.string().max(200).nullable(),
    cta: z.string().max(200).nullable(),
    label: z.string().max(120).nullable(),
    isActive: z.boolean(),
  })
  .partial();
export type MorningHolidayUpdateInput = z.infer<typeof MorningHolidayUpdateInput>;

export const MorningHolidayDto = z.object({
  id: z.uuid(),
  cityId: z.uuid().nullable(),
  countryCode: z.string().nullable(),
  date: z.string(),
  imageUrl: z.string().nullable(),
  greeting: z.string().nullable(),
  cta: z.string().nullable(),
  label: z.string().nullable(),
  isActive: z.boolean(),
});
export type MorningHolidayDto = z.infer<typeof MorningHolidayDto>;

// --- preview / test ---
export const MorningPreviewDto = z.object({
  imageUrl: z.string().nullable(),
  caption: z.string(),
});
export type MorningPreviewDto = z.infer<typeof MorningPreviewDto>;

export const MorningTestSendInput = z.object({
  cityId: z.uuid(),
  date: z.iso.date().optional(),
});
export type MorningTestSendInput = z.infer<typeof MorningTestSendInput>;
